import type { CompanyShellCapabilities } from "@/services/companyShell";
import type {
  EmployeeImportCapabilityEnvelope,
} from "@/services/employeeImport/employeeImportCapabilities";

export type EmployeeOnboardingCapabilities = {
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;

  mayInviteEmployees: boolean;
  mayBulkProvisionEmployees: boolean;

  showIndividualInvitation: boolean;
  showBulkProvisioning: boolean;

  hasAnyOnboardingAccess: boolean;
};

export type EmployeeOnboardingCapabilityInputs = {
  shellCapabilities: CompanyShellCapabilities | null;
  importCapabilities: EmployeeImportCapabilityEnvelope | null;
};

export type ResolveEmployeeOnboardingCapabilitiesInput =
  EmployeeOnboardingCapabilityInputs & {
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
  };

export function canAccessIndividualEmployeeInvite({
  shellCapabilities,
}: EmployeeOnboardingCapabilityInputs): boolean {
  return !!shellCapabilities?.can_invite_employees;
}

export function canAccessBulkEmployeeProvisioning({
  shellCapabilities,
  importCapabilities,
}: EmployeeOnboardingCapabilityInputs): boolean {
  return !!(
    shellCapabilities?.can_invite_employees &&
    importCapabilities?.context === "bulk_workforce_provisioning" &&
    importCapabilities.canUploadImportFile
  );
}

export function hasAnyEmployeeOnboardingAccess(
  inputs: EmployeeOnboardingCapabilityInputs
): boolean {
  return (
    canAccessIndividualEmployeeInvite(inputs) ||
    canAccessBulkEmployeeProvisioning(inputs)
  );
}

export function canAccessEmployeeOnboarding(
  inputs: EmployeeOnboardingCapabilityInputs
): boolean {
  return hasAnyEmployeeOnboardingAccess(inputs);
}

export function resolveEmployeeOnboardingCapabilities(
  input: ResolveEmployeeOnboardingCapabilitiesInput
): EmployeeOnboardingCapabilities {
  const mayInviteEmployees =
    canAccessIndividualEmployeeInvite(input);

  const mayBulkProvisionEmployees =
    canAccessBulkEmployeeProvisioning(input);

  return {
    loading: input.loading,
    error: input.error,
    refresh: input.refresh,

    mayInviteEmployees,
    mayBulkProvisionEmployees,

    showIndividualInvitation: mayInviteEmployees,
    showBulkProvisioning: mayBulkProvisionEmployees,

    hasAnyOnboardingAccess:
      mayInviteEmployees ||
      mayBulkProvisionEmployees,
  };
}